import React, { useState, useRef, useEffect, useMemo } from 'react';

/*
  A styled replacement for <select>. Browsers do not allow styling the native
  <option> list, so the list is rebuilt here as a real menu: each row can carry
  an icon and a secondary line, long lists get a filter box, and the whole
  thing is keyboard driven.

  Because this is not a native control, `required` no longer applies — the
  caller must validate the value before submitting.

  options: [{ value, label, meta?, icon? }]
*/
function DivineSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  ariaLabel,
  disabled = false,
  emptyText = 'Nothing to choose from',
  searchable
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const boxRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  /* A filter only earns its place once the list is long enough to scroll */
  const showSearch = searchable === undefined ? options.length > 6 : searchable;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return options;

    return options.filter((option) =>
      `${option.label} ${option.meta || ''}`.toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = options.find((option) => option.value === value) || null;

  /* Close on outside click */
  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);

    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  /* Focus the filter as soon as the menu opens */
  useEffect(() => {
    if (open && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, showSearch]);

  /* Keep the highlighted row in view while arrow-keying */
  useEffect(() => {
    if (!open || !menuRef.current) return;

    const node = menuRef.current.children[activeIndex];

    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openMenu = () => {
    if (disabled) return;

    setQuery('');
    setActiveIndex(Math.max(0, filtered.findIndex((o) => o.value === value)));
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (option) => {
    onChange(option.value);
    closeMenu();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeMenu();

      return;
    }

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }

      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (filtered.length ? (i + 1) % filtered.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        filtered.length ? (i - 1 + filtered.length) % filtered.length : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();

      if (filtered[activeIndex]) pick(filtered[activeIndex]);
    } else if (e.key === 'Tab') {
      closeMenu();
    }
  };

  return (
    <div
      className={`dvs${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={boxRef}
    >
      <button
        type="button"
        className="dvs-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleKeyDown}
      >
        {selected?.icon && (
          <span className="dvs-trigger-ico" aria-hidden="true">
            {selected.icon}
          </span>
        )}

        <span
          className={`dvs-trigger-text${selected ? '' : ' is-placeholder'}`}
        >
          {selected ? selected.label : placeholder}
        </span>

        <span className="dvs-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="dvs-panel">
          {showSearch && (
            <div className="dvs-search">
              <span className="dvs-search-ico" aria-hidden="true">
                🔍
              </span>
              <input
                ref={searchRef}
                type="text"
                className="dvs-search-input"
                placeholder="Type to filter…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyDown}
              />
              {query && (
                <button
                  type="button"
                  className="dvs-search-clear"
                  aria-label="Clear filter"
                  onClick={() => {
                    setQuery('');
                    setActiveIndex(0);
                    searchRef.current?.focus();
                  }}
                >
                  ×
                </button>
              )}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="dvs-empty">
              <span className="dvs-empty-ico">🪷</span>
              {options.length === 0 ? emptyText : 'No matches'}
            </div>
          ) : (
            <ul className="dvs-menu" role="listbox" ref={menuRef}>
              {filtered.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  className={`dvs-option${
                    option.value === value ? ' is-selected' : ''
                  }${index === activeIndex ? ' is-active' : ''}`}
                  style={{ animationDelay: `${Math.min(index, 9) * 40}ms` }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(option)}
                >
                  <span className="dvs-option-ico" aria-hidden="true">
                    {option.icon || '✦'}
                  </span>

                  <span className="dvs-option-body">
                    <span className="dvs-option-label">{option.label}</span>
                    {option.meta && (
                      <span className="dvs-option-meta">{option.meta}</span>
                    )}
                  </span>

                  <span className="dvs-tick" aria-hidden="true">
                    ✓
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <style>{divineSelectStyles}</style>
    </div>
  );
}

const divineSelectStyles = `
  .dvs { position: relative; }
  .dvs.is-open { z-index: 60; }

  /* ===== Trigger ===== */
  .dvs-trigger {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    border-radius: 14px;
    padding: 12px 15px;
    font-family: inherit;
    font-size: 1rem;
    color: #431407;
    border: 1px solid #eadfce;
    background: #fffdfa;
    cursor: pointer;
    transition: border-color 0.25s ease, box-shadow 0.25s ease,
      background 0.25s ease;
  }
  .dvs-trigger:hover:not(:disabled) {
    border-color: #f59e0b;
    background: #fff;
  }
  .dvs.is-open .dvs-trigger,
  .dvs-trigger:focus-visible {
    outline: none;
    background: #fff;
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234, 88, 12, 0.15);
  }
  .dvs.is-disabled .dvs-trigger {
    background: #f6f1ea;
    color: #a8a29e;
    cursor: not-allowed;
  }
  .dvs-trigger-ico { flex: none; font-size: 1.05rem; line-height: 1; }
  .dvs-trigger-text {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dvs-trigger-text.is-placeholder { color: #b8a894; }
  .dvs-caret {
    flex: none;
    color: #b45309;
    font-size: 0.85rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.25s ease;
  }
  .dvs.is-open .dvs-caret {
    transform: rotate(180deg) scale(1.15);
    color: #ea580c;
  }

  /* ===== Panel ===== */
  .dvs-panel {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 60;
    padding: 8px;
    border-radius: 18px;
    border: 1px solid rgba(245, 158, 11, 0.35);
    background:
      radial-gradient(420px 120px at 15% -20%, rgba(251, 191, 36, 0.2), transparent 70%),
      linear-gradient(180deg, #fffdf7 0%, #fff7ea 100%);
    box-shadow: 0 24px 50px rgba(124, 45, 18, 0.24),
      0 0 0 1px rgba(255, 255, 255, 0.6) inset;
    transform-origin: top center;
    animation: dvsPanelIn 0.3s cubic-bezier(0.22, 1.4, 0.4, 1) both;
  }
  /* Shimmering gold thread along the top edge */
  .dvs-panel::before {
    content: '';
    position: absolute;
    left: 14px; right: 14px; top: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg,
      transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 200% 100%;
    animation: dvsThread 2.6s linear infinite;
  }
  @keyframes dvsPanelIn {
    from { opacity: 0; transform: translateY(-10px) scaleY(0.88); }
    to   { opacity: 1; transform: translateY(0) scaleY(1); }
  }
  @keyframes dvsThread {
    to { background-position: 200% 0; }
  }

  /* ===== Filter ===== */
  .dvs-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    padding: 8px 12px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #fde68a;
  }
  .dvs-search-ico { font-size: 0.85rem; opacity: 0.7; }
  .dvs-search-input {
    flex: 1 1 auto;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.9rem;
    color: #431407;
  }
  .dvs-search-input::placeholder { color: #c4b5a0; }
  .dvs-search-clear {
    flex: none;
    border: none;
    background: #fff7ed;
    color: #b45309;
    width: 20px; height: 20px;
    border-radius: 50%;
    line-height: 1;
    font-size: 0.95rem;
    cursor: pointer;
  }
  .dvs-search-clear:hover { background: #fde68a; }

  /* ===== Options ===== */
  .dvs-menu {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
  }
  .dvs-option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 11px;
    border-radius: 12px;
    cursor: pointer;
    overflow: hidden;
    color: #431407;
    animation: dvsOptionIn 0.3s ease both;
    transition: color 0.2s ease, transform 0.2s ease;
  }
  /* Divine wash unrolling under the highlighted row */
  .dvs-option::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    background: linear-gradient(120deg, #3b0764 0%, #7c2d12 55%, #b45309 100%);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .dvs-option.is-active { color: #fff7ea; transform: translateX(3px); }
  .dvs-option.is-active::before { transform: scaleX(1); }
  @keyframes dvsOptionIn {
    from { opacity: 0; transform: translateX(-10px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .dvs-option-ico {
    position: relative;
    z-index: 1;
    flex: none;
    width: 30px; height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    font-size: 0.95rem;
    background: linear-gradient(135deg, #fff7ed, #fde68a);
    box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .dvs-option.is-active .dvs-option-ico { transform: scale(1.1) rotate(-7deg); }

  .dvs-option-body {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  .dvs-option-label {
    font-size: 0.94rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Second line — what tells two same-named rows apart */
  .dvs-option-meta {
    font-size: 0.72rem;
    opacity: 0.66;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dvs-tick {
    position: relative;
    z-index: 1;
    flex: none;
    font-size: 0.85rem;
    font-weight: 900;
    color: #16a34a;
    opacity: 0;
    transform: scale(0.3) rotate(-90deg);
    transition: opacity 0.25s ease,
      transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .dvs-option.is-selected .dvs-tick { opacity: 1; transform: scale(1) rotate(0deg); }
  .dvs-option.is-selected { font-weight: 700; }
  .dvs-option.is-selected.is-active .dvs-tick { color: #86efac; }

  .dvs-empty {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 16px 12px;
    font-size: 0.88rem;
    color: #9a3412;
  }
  .dvs-empty-ico { font-size: 1.1rem; }

  .dvs-menu::-webkit-scrollbar { width: 6px; }
  .dvs-menu::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: linear-gradient(180deg, #fbbf24, #b45309);
  }

  @media (prefers-reduced-motion: reduce) {
    .dvs-panel, .dvs-option { animation-duration: 0.01ms; }
    .dvs-panel::before { animation: none; }
  }
`;

export default DivineSelect;
